/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { Button, Tooltip, TooltipContent, TooltipTrigger, useIsMobile, useSidebar } from '@quadminds/mindkit';
import { MindyDocRoles, MindyStandardRoles } from '@quadminds/mindy-shared';
import { IAuthMeV2OutputDto } from '@quadminds/shared/auth-system';
import { useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { FileUp, MessageSquarePlus } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useByPassContext } from '../../../../providers/ByPass/ByPass.provider';
import { useChatContext } from '../../../../providers/Chat/Chat.provider';

export function NavButtons() {
    const isMobile = useIsMobile();
    const { toggleSidebar, state, setOpen } = useSidebar();
    const { setReset, isPending: isQuestionPending, isStream: isQuestionStream } = useChatContext();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const queryClient = useQueryClient();
    const user: IAuthMeV2OutputDto | undefined = queryClient.getQueryData(['userInfo']);
    const { isImpersonated } = useByPassContext();
    const currentUserRoles = user?.roles || [];
    const baseKnowledgeUrl = user?.currentOrganization;
    const rolesSet = new Set(currentUserRoles);
    const showKnowledgeManagerBtn =
        rolesSet.has(MindyStandardRoles.ADMIN) ||
        rolesSet.has(MindyStandardRoles.MANAGER) ||
        rolesSet.has(MindyDocRoles.ADMIN_DOC) ||
        rolesSet.has(MindyDocRoles.MANAGER_DOC) ||
        isImpersonated;

    const handleReset = useCallback(() => {
        if (state === 'collapsed') {
            setOpen(true);
        }
        setReset(true);
        navigate('/chat');
        if (isMobile) {
            toggleSidebar();
        }
    }, [state, setOpen, setReset, navigate, isMobile, toggleSidebar]);

    const handleNavigateKnowledge = useCallback(() => {
        if (state === 'collapsed') {
            setOpen(true);
        }
        navigate(`/knowledge/${baseKnowledgeUrl}`);
    }, [state, setOpen, navigate, baseKnowledgeUrl]);

    const buttonVariants = useMemo(
        () => ({
            collapsed: {
                width: 'auto',
                transition: { duration: 0.3, ease: 'easeInOut' },
            },
            expanded: {
                width: '100%',
                transition: { duration: 0.3, ease: 'easeInOut' },
            },
        }),
        [],
    );

    const textVariants = useMemo(
        () => ({
            hidden: {
                opacity: 0,
                x: -10,
                transition: { duration: 0.1 },
            },
            visible: {
                opacity: 1,
                x: 0,
                transition: { duration: 0.3, delay: 0.1 },
            },
        }),
        [],
    );

    const renderButtons = useMemo(() => {
        return (
            <AnimatePresence mode="wait" initial={state === 'collapsed'}>
                {state === 'collapsed' ? (
                    <motion.div
                        key="collapsed"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.2 }}
                        className="flex flex-col items-center space-y-4"
                    >
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    size="icon"
                                    className="rounded-full"
                                    onClick={handleReset}
                                    data-testid="new-conversation"
                                >
                                    <MessageSquarePlus />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent className="!-mt-8" side="right">
                                {t('sideBar.newChat')}
                            </TooltipContent>
                        </Tooltip>
                        {showKnowledgeManagerBtn && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        size="icon"
                                        className="rounded-full"
                                        variant="secondary"
                                        disabled={baseKnowledgeUrl === undefined}
                                        onClick={handleNavigateKnowledge}
                                        data-testid="my-knowledge-manager"
                                    >
                                        <FileUp />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent className="!-mt-8" side="right">
                                    {t('sideBar.knowledgeManager')}
                                </TooltipContent>
                            </Tooltip>
                        )}
                    </motion.div>
                ) : (
                    <motion.div
                        key="expanded"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                        className="space-y-4"
                    >
                        <motion.div variants={buttonVariants} initial="collapsed" animate="expanded">
                            <Button
                                disabled={isQuestionPending || isQuestionStream}
                                className="rounded-full flex items-center w-full justify-start disabled:opacity-100 disabled:pointer-events-auto disabled:cursor-not-allowed"
                                onClick={handleReset}
                                data-testid="new-conversation"
                            >
                                <div className="flex items-center w-full">
                                    <div className="w-6 h-6 flex items-center justify-center mr-2">
                                        <MessageSquarePlus size={22} />
                                    </div>
                                    <motion.span variants={textVariants} initial="hidden" animate="visible">
                                        {t('sideBar.newChat')}
                                    </motion.span>
                                </div>
                            </Button>
                        </motion.div>
                        {showKnowledgeManagerBtn && (
                            <motion.div variants={buttonVariants} initial="collapsed" animate="expanded">
                                <Button
                                    className="rounded-full flex items-center w-full justify-start disabled:opacity-100 disabled:pointer-events-auto disabled:cursor-not-allowed"
                                    variant="secondary"
                                    disabled={baseKnowledgeUrl === undefined || isQuestionPending || isQuestionStream}
                                    onClick={handleNavigateKnowledge}
                                    data-testid="my-knowledge-manager"
                                >
                                    <div className="flex items-center w-full">
                                        <div className="w-6 h-6 flex items-center justify-center mr-2">
                                            <FileUp size={22} />
                                        </div>
                                        <motion.span variants={textVariants} initial="hidden" animate="visible">
                                            {t('sideBar.knowledgeManager')}
                                        </motion.span>
                                    </div>
                                </Button>
                            </motion.div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        );
    }, [
        state,
        t,
        handleReset,
        handleNavigateKnowledge,
        showKnowledgeManagerBtn,
        baseKnowledgeUrl,
        buttonVariants,
        textVariants,
        isQuestionPending,
        isQuestionStream,
    ]);

    return <div className="p-2.5 space-y-4">{renderButtons}</div>;
}

export default NavButtons;
